/**
 * PostElimPlacementUI — interrupt placement phase triggered when an attacker
 * eliminates a player mid-attack.
 *
 * Budget = state.bonusArmies[myPlayerId] only. No base or region bonus is
 * added — the engine handles the accumulation and the client reads the value
 * directly. After the player commits, the server flips phase back to 'attack'
 * so they can continue pressing their advantage.
 *
 * Visually distinct from ReinforceUI: an amber/blood accent banner explains
 * the interrupt context so the player immediately understands this is a special
 * moment, not their normal placement turn.
 */

import React, { useState, useMemo, useCallback } from 'react'
import { View } from 'react-native'
import { MapView } from '../map/MapView'
import { MapControlsBarSlot } from '../map/MapControlsBar'
import { PlacementHud, type PlacementStepMode } from '../map/PlacementHud'
import {
  useGameMapSceneRegistration,
  type GameMapSceneRegistration,
} from '../map/gameMapScene'
import { placementBonusText } from '../../lib/placementBonus'
import { useMapDimensions } from '../../lib/useMapDimensions'
import { usePostElimPlace } from '../../hooks/useGames'
import { FreeFortifyPanel } from './FreeFortifyPanel'
import { freeFortifyActionable } from '../../lib/placementFlow'
import {
  derivePendingCounts,
  totalStackArmies,
  stackToPlacementList,
  type PlacementAction,
} from '../../lib/placementStack'
import type { GameState } from '../../lib/gameState'
import { armyHeadroom, placeableArmies } from '@warcarp/api-types'
import { placeableTerritories } from '../../lib/teamPlay'
import type { GamePlayerResponse } from '@warcarp/api-types'
import type { PanOffset } from '../../lib/mapZoom'

interface PostElimPlacementUIProps {
  gameId: string
  state: GameState
  myPlayerId: string
  slotByPlayerId: Record<string, number>
  /** For the on-map player legend. */
  gamePlayers: GamePlayerResponse[]
  viewerUserId?: string
  /** Forwarded to MapView so the player-roster strip centres on the
   * map, not the screen (HUD occupies bottom/side during placement). */
  mapRightOverlay?: React.ReactNode
  regionToggleNode?: React.ReactNode
  regionToggleCorner?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
  zoomToggleNode?: React.ReactNode
  zoomToggleCorner?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
  /**
   * Optional header rendered at the TOP of the landscape action panel
   * (above the HUD). The game screen colocates the cog + horizontal
   * roster strip here so neither floats over the map.
   */
  actionPanelHeader?: React.ReactNode
  /**
   * Optional cards HUD chip — docked in the placement HUD's top-right corner.
   * Mirrors the same prop in ReinforceUI: the chip hides when this UI doesn't
   * render, so the player only sees it while cards are actionable.
   */
  cardChip?: React.ReactNode
  /** Thin control bar rendered directly beneath the map (zoom + view mode). */
  mapControlsBar?: React.ReactNode
  /** Forwarded to MapView — zoom-and-pan state managed by the game screen. */
  zoomEnabled?: boolean
  panOffset?: PanOffset
  onPanChange?: (offset: PanOffset) => void
  zoomScale?: number
  onZoomScaleChange?: (scale: number) => void
  /** Forwarded to MapView for bot-attack animation fix (see useAttackEvents). */
  viewerJoinedAt?: number
  /** Time-machine hook — scrubs the game screen into the snapshot view at the given event index. */
  onViewAtEvent?: (eventIndex: number) => void
  selectedEventIndex?: number | null
  /**
   * Optional CONTROLLED pending-placement state (see ReinforceUI) so staged
   * placements survive the regions overlay unmounting this component. When
   * omitted, the component manages its own local state.
   */
  placementStack?: PlacementAction[]
  onPlacementStackChange?: React.Dispatch<React.SetStateAction<PlacementAction[]>>
  stepMode?: PlacementStepMode
  onStepModeChange?: React.Dispatch<React.SetStateAction<PlacementStepMode>>
  /** When set, the parent owns the single shared MapView. */
  mapSceneKey?: string
  onMapSceneConfigChange?: GameMapSceneRegistration
}

export function PostElimPlacementUI({
  gameId,
  state,
  myPlayerId,
  slotByPlayerId,
  gamePlayers,
  viewerUserId: _viewerUserId,
  mapRightOverlay,
  regionToggleNode,
  regionToggleCorner,
  zoomToggleNode,
  zoomToggleCorner,
  actionPanelHeader,
  cardChip,
  mapControlsBar,
  zoomEnabled,
  panOffset,
  onPanChange,
  zoomScale,
  onZoomScaleChange,
  viewerJoinedAt,
  placementStack: placementStackProp,
  onPlacementStackChange,
  stepMode: stepModeProp,
  onStepModeChange,
  mapSceneKey,
  onMapSceneConfigChange,
}: PostElimPlacementUIProps) {
  const postElimPlace = usePostElimPlace(gameId)
  const viewerSlot = slotByPlayerId[myPlayerId] ?? 1

  // Budget is the elimination bonus only — read directly from state.
  // Do NOT derive base + region here; the engine owns that math.
  const rawBudget = state.bonusArmies?.[myPlayerId] ?? 0
  // Troop caps can leave less room than the budget; the server requires the
  // placed total to match what actually FITS, so the Commit button has to agree
  // or the player is stuck with armies they can never place.
  // Placeable = own territories plus, in a team game, teammates' (the
  // server's canReinforce uses the same set for the cap headroom).
  const budget = placeableArmies(rawBudget, placeableTerritories(state, myPlayerId))

  // Own territories, plus a teammate's in a team game (canPlaceOn).
  const myTerritories = useMemo(
    () => placeableTerritories(state, myPlayerId),
    [state, myPlayerId],
  )
  const myTerritoryIds = useMemo(() => myTerritories.map((t) => t.id), [myTerritories])

  // Stack-of-actions model mirrors ReinforceUI: each tap pushes
  // { territoryId, armies }, undo pops the tail, reset clears all. Controlled by
  // the parent when provided (so placements survive the regions overlay
  // unmounting this component); otherwise local.
  const [localStack, setLocalStack] = useState<PlacementAction[]>([])
  const placementStack = placementStackProp ?? localStack
  const setPlacementStack = onPlacementStackChange ?? setLocalStack
  const [localStepMode, setLocalStepMode] = useState<PlacementStepMode>(1)
  const stepMode = stepModeProp ?? localStepMode
  const setStepMode = onStepModeChange ?? setLocalStepMode

  const pendingCounts = useMemo(
    () => derivePendingCounts(placementStack),
    [placementStack],
  )

  const armyOverrides = useMemo(() => {
    const out: Record<string, number> = {}
    for (const t of myTerritories) {
      const pending = pendingCounts[t.id] ?? 0
      if (pending > 0) out[t.id] = t.armies + pending
    }
    return out
  }, [myTerritories, pendingCounts])

  const totalPlaced = useMemo(
    () => totalStackArmies(placementStack),
    [placementStack],
  )
  const remaining = budget - totalPlaced

  // Free fortify must be resolved (move armies or skip) before the player can
  // place bonus armies. While it's pending the map is NOT tappable for
  // placement; tapping surfaces a brief notice instead. Actionability (a
  // lastConquest pair AND a source with an army to spare) is computed by the
  // shared helper so this UI and the turn-in auto-open gate agree exactly.
  const freeFortifyPending = freeFortifyActionable(state.lastConquest, state.map.territories)

  const handleTerritoryPress = useCallback(
    (territoryId: string) => {
      // Free fortify must be resolved (move armies or skip) before placement —
      // ignore territory taps until then.
      if (freeFortifyPending) return
      if (!myTerritoryIds.includes(territoryId)) return
      if (remaining <= 0) return
      const requested = stepMode === 'max' ? remaining : stepMode
      // Also clamp to the territory's troop cap, counting what's already
      // pending. Uncapped reports Infinity. The server rejects an over-cap
      // placement outright, so one bad tap would fail the whole commit.
      const territory = state.map.territories.find((t) => t.id === territoryId)
      const pending = placementStack
        .filter((p) => p.territoryId === territoryId)
        .reduce((sum, p) => sum + p.armies, 0)
      const headroom = territory
        ? armyHeadroom({ armies: territory.armies + pending, maxArmies: territory.maxArmies })
        : Infinity
      const delta = Math.min(requested, remaining, headroom)
      if (delta <= 0) return
      setPlacementStack((prev) => [...prev, { territoryId, armies: delta }])
    },
    [freeFortifyPending, myTerritoryIds, remaining, stepMode, state.map.territories, placementStack],
  )

  async function handleCommit() {
    const placementList = stackToPlacementList(placementStack)
    try {
      await postElimPlace.mutateAsync({ placements: placementList })
    } catch {
      // Errors surface via postElimPlace.isError; the toast system in the
      // parent game screen handles display if needed.
    }
  }

  function handleReset() {
    setPlacementStack([])
  }

  function handleUndo() {
    setPlacementStack((prev) => prev.slice(0, -1))
  }

  const { isLandscape, isDesktop, actionPanelWidth } = useMapDimensions(state)

  // Interrupt context banner — shown above the HUD so the player
  // immediately understands why the attack phase paused. Distinct
  // from the normal placement header so there's no confusion about
  // phase context. Uses a blood-tinted accent to communicate urgency.
  // When free fortify is pending, show the FreeFortifyPanel as the primary
  // step and hide the bonus-placement controls. Once lastConquest clears
  // (transfer or skip confirmed by the server round-trip), freeFortifyPending
  // becomes false and the normal placement HUD appears.
  const hud = freeFortifyPending ? (
    <View className={isLandscape ? 'p-2' : 'mx-2 mt-2'}>
      <FreeFortifyPanel
        gameId={gameId}
        state={state}
        // Bonus armies waiting after the fortify — drives the "fortify first,
        // then place your bonus" two-step copy. See freeFortifyCopy.
        bonusArmiesAfter={budget}
      />
    </View>
  ) : (
    <View
      className={isLandscape ? 'p-2' : 'mx-2 mt-2'}
    >
      <PlacementHud
        remaining={remaining}
        budget={budget}
        onReset={handleReset}
        onUndo={handleUndo}
        onCommit={handleCommit}
        canReset={totalPlaced > 0 && !postElimPlace.isPending}
        canUndo={placementStack.length > 0 && !postElimPlace.isPending}
        canCommit={totalPlaced === budget && !postElimPlace.isPending}
        isCommitting={postElimPlace.isPending}
        stepMode={stepMode}
        onStepModeChange={setStepMode}
        // Subtitle breaks the bonus down by source (elimination + card turn-in);
        // see placementBonusText. The budget (total to place) is passed separately.
        bonusText={placementBonusText(state, myPlayerId, state.phase)}
        cardChip={cardChip}
      />
    </View>
  )

  useGameMapSceneRegistration({
    key: mapSceneKey,
    onChange: onMapSceneConfigChange,
    config: {
      validTargetIds: freeFortifyPending ? [] : myTerritoryIds,
      armyOverrides,
      onTerritoryPress: handleTerritoryPress,
    },
  })

  const landscapeHud = (
    <View style={{ width: actionPanelWidth }} className="flex-col">
      {actionPanelHeader}
      {hud}
    </View>
  )

  if (mapSceneKey && onMapSceneConfigChange) {
    return isLandscape ? landscapeHud : hud
  }

  return (
    <View className={isLandscape ? 'flex-1 flex-row' : 'flex-1'}>
      {isLandscape ? landscapeHud : null}
      {/* Landscape-only centring; portrait keeps map's explicit height
          so the HUD below gets its 220 px reserve. See AttackUI.
          Desktop pins the map flush to the top (matches the map
          explorer); phone landscape keeps vertical centring. */}
      {isLandscape ? (
        <View className="flex-1 border-l border-olive bg-paper">
          <View className={`${isDesktop ? 'items-start' : 'flex-1 items-start justify-center'}`}>
            <MapView
              state={state}
              viewerSlot={viewerSlot}
              validTargetIds={freeFortifyPending ? [] : myTerritoryIds}
              armyOverrides={armyOverrides}
              onTerritoryPress={handleTerritoryPress}
              rightOverlay={mapRightOverlay}
              regionToggleNode={regionToggleNode}
              regionToggleCorner={regionToggleCorner}
              zoomToggleNode={zoomToggleNode}
              zoomToggleCorner={zoomToggleCorner}
              hideOnMapZoomButtons={!!mapControlsBar}
              reservedBottom={mapControlsBar ? 44 : undefined}
              zoomEnabled={zoomEnabled}
              panOffset={panOffset}
              onPanChange={onPanChange}
              zoomScale={zoomScale}
              onZoomScaleChange={onZoomScaleChange}
              viewerJoinedAt={viewerJoinedAt}
            />
          </View>
          <MapControlsBarSlot state={state}>{mapControlsBar}</MapControlsBarSlot>
        </View>
      ) : (
        <MapView
          state={state}
          viewerSlot={viewerSlot}
          validTargetIds={freeFortifyPending ? [] : myTerritoryIds}
          armyOverrides={armyOverrides}
          onTerritoryPress={handleTerritoryPress}
          rightOverlay={mapRightOverlay}
          regionToggleNode={regionToggleNode}
          regionToggleCorner={regionToggleCorner}
          zoomToggleNode={zoomToggleNode}
          zoomToggleCorner={zoomToggleCorner}
          hideOnMapZoomButtons={!!mapControlsBar}
          zoomEnabled={zoomEnabled}
          panOffset={panOffset}
          onPanChange={onPanChange}
          zoomScale={zoomScale}
          onZoomScaleChange={onZoomScaleChange}
          viewerJoinedAt={viewerJoinedAt}
        />
      )}
      {!isLandscape && mapControlsBar}
      {!isLandscape && hud}
    </View>
  )
}
